import * as React from "react";
import { getLabelForArtworkStatus } from "../../../../../helpers/getLabelForArtworkStatus";
import { green } from "@mui/material/colors";
import { grey } from "@mui/material/colors";
import { makeStyles, withStyles } from "@mui/styles";
import {
  markOrderAsArtworkApprovedMutation,
  markOrderAsArtworkReviewedMutation,
  markOrderAsArtworkStartedMutation,
  markOrderAsNoLongerOnArtworkHoldMutation,
  markOrderAsNoLongerOnHoldMutation,
  markOrderAsOnArtworkHoldMutation,
  markOrderAsProofCompletedMutation,
  setOrderAutoApprovedMutation
} from "../../../../../graph";
import { red } from "@mui/material/colors";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import JobStatusIcon from "../../../../../components/JobStatusIcon";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PlaceOrderOnHoldModal from "../../../../../components/PlaceOrderOnHoldModal";
import RequestArtworkChangeModal from "../../../../../components/RequestArtworkChangeModal";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import withSnackbar from "../../../../../components/withSnackbar";

const GreenSwitch = withStyles({
  switchBase: {
    color: grey[300],
    "&$checked": {
      color: green[500]
    }
  },
  checked: {}
})(Switch);

const RedSwitch = withStyles({
  switchBase: {
    color: grey[300],
    "&$checked": {
      color: red[500]
    }
  },
  checked: {}
})(Switch);

const useStyles = makeStyles(theme => ({
  autoApprovedContainer: {
    display: "flex",
    alignItems: "center"
  },

  statusHeader: {
    marginRight: theme.spacing(1)
  },

  buttonContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: theme.spacing(1)
  },

  jobStatus: {
    marginLeft: theme.spacing(1)
  },

  iconSwitchContainer: {
    display: "flex",
    alignItems: "center"
  },

  spaceBetweenContainer: {
    display: "flex"
  },

  spacerRight: {
    marginRight: theme.spacing(1)
  },

  spinnerWrapper: {
    padding: theme.spacing(1),
    position: "relative",
    textAlign: "center"
  }
}));

const ArtOrderStatus = ({
  order: {
    artworkStatus,
    autoApproved,
    canViewerApproveArtwork,
    canViewerCompleteProof,
    canViewerPlaceOnArtworkHold,
    canViewerPlaceOnHold,
    canViewerRemoveArtworkHold,
    canViewerRemoveHold,
    canViewerRequestArtworkChange,
    canViewerReviewArtwork,
    canViewerStartArtwork,
    id: orderId
  },
  showSuccessSnackbar,
  showErrorSnackbar
}) => {
  const isOrderOnHold = artworkStatus === "ORDER_ON_HOLD";

  const classes = useStyles();

  // Mutations:
  const [markOrderAsArtworkApproved] = useMutation(
    markOrderAsArtworkApprovedMutation
  );
  const [markOrderAsArtworkReviewed] = useMutation(
    markOrderAsArtworkReviewedMutation
  );
  const [markOrderAsArtworkStarted] = useMutation(
    markOrderAsArtworkStartedMutation
  );
  const [markOrderAsNoLongerOnArtworkHold] = useMutation(
    markOrderAsNoLongerOnArtworkHoldMutation
  );
  const [markOrderAsNoLongerOnHold] = useMutation(
    markOrderAsNoLongerOnHoldMutation
  );
  const [markOrderAsOnArtworkHold] = useMutation(
    markOrderAsOnArtworkHoldMutation
  );
  const [markOrderAsProofCompleted] = useMutation(
    markOrderAsProofCompletedMutation
  );
  const [setOrderAutoApproved] = useMutation(setOrderAutoApprovedMutation);

  // States:
  const [isMutatingOrder, setIsMutatingOrder] = useState(false);
  const [isPlacingOrderOnHold, setIsPlacingOrderOnHold] = useState(false);
  const [isRequestingArtworkChange, setIsRequestingArtworkChange] =
    useState(false);
  const [isSettingAutoApproved, setIsSettingAutoApproved] = useState(null);

  // Handlers:
  const handleHoldToggleSwitch = () => setIsPlacingOrderOnHold(true);

  const handlePlaceOrderOnHoldModalRequestClose = () =>
    setIsPlacingOrderOnHold(false);

  const handleAutoApprovedToggleSwitch = () => {
    setIsSettingAutoApproved(true);
    setOrderAutoApproved({
      variables: { autoApproved: !autoApproved, orderId }
    })
      .then(
        ({
          data: {
            setOrderAutoApproved: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsSettingAutoApproved(false));
  };

  const handleRemoveHoldToggleSwitch = () => {
    setIsMutatingOrder(true);
    markOrderAsNoLongerOnHold({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsNoLongerOnHold: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleApproveArtworkButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsArtworkApproved({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsArtworkApproved: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleReviewArtworkButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsArtworkReviewed({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsArtworkReviewed: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handlePlaceOnArtworkHoldButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsOnArtworkHold({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsOnArtworkHold: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleRemoveArtworkHoldButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsNoLongerOnArtworkHold({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsNoLongerOnArtworkHold: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleRequestArtworkChangeButtonClick = () =>
    setIsRequestingArtworkChange(true);

  const handleRequestArtworkChangeModalRequestClose = () =>
    setIsRequestingArtworkChange(false);

  const handleStartButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsArtworkStarted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsArtworkStarted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleProofCompletedButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsProofCompleted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsProofCompleted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  return (
    <div>
      <div className={classes.spaceBetweenContainer}>
        <JobStatusIcon status={artworkStatus} />
        <div className={classes.jobStatus}>
          <div className={classes.autoApprovedContainer}>
            <Typography className={classes.statusHeader} variant="h6">
              Art
            </Typography>
            <FormControlLabel
              control={
                isSettingAutoApproved ? (
                  <div className={classes.spinnerWrapper}>
                    <CircularProgress size={20} />
                  </div>
                ) : (
                  <GreenSwitch
                    checked={autoApproved}
                    onChange={handleAutoApprovedToggleSwitch}
                  />
                )
              }
              label={autoApproved ? "ART AUTO APPROVED" : "AUTO APPROVE ORDER"}
            />
          </div>
          <Typography variant="body2">
            {getLabelForArtworkStatus(artworkStatus)}
          </Typography>
        </div>
      </div>
      {isMutatingOrder ? (
        <div className={classes.spinnerWrapper}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.buttonContainer}>
          {canViewerStartArtwork && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleStartButtonClick}
            >
              Start
            </Button>
          )}
          {canViewerPlaceOnArtworkHold && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handlePlaceOnArtworkHoldButtonClick}
            >
              Place On Artwork Hold
            </Button>
          )}
          {canViewerRemoveArtworkHold && (
            <Button
              className={classes.spacerRight}
              variant="contained"
              onClick={handleRemoveArtworkHoldButtonClick}
            >
              Remove Artwork Hold
            </Button>
          )}
          {canViewerApproveArtwork && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleApproveArtworkButtonClick}
            >
              Approve
            </Button>
          )}
          {canViewerReviewArtwork && (
            <Button
              className={classes.spacerRight}
              variant="contained"
              onClick={handleReviewArtworkButtonClick}
            >
              Review
            </Button>
          )}
          {canViewerRequestArtworkChange && (
            <Button
              className={classes.spacerRight}
              variant="contained"
              onClick={handleRequestArtworkChangeButtonClick}
            >
              Request Changes
            </Button>
          )}
          {canViewerCompleteProof && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleProofCompletedButtonClick}
            >
              Proof Complete
            </Button>
          )}
        </div>
      )}
      <div>
        {(canViewerPlaceOnHold || canViewerRemoveHold) && (
          <div className={classes.iconSwitchContainer}>
            <div className={classes.spacerRight}>
              {isOrderOnHold ? <LockIcon /> : <LockOpenIcon />}
            </div>
            <FormControlLabel
              control={
                isPlacingOrderOnHold ? (
                  <div className={classes.spinnerWrapper}>
                    <CircularProgress size={20} />
                  </div>
                ) : (
                  <RedSwitch
                    checked={isOrderOnHold}
                    onChange={
                      isOrderOnHold
                        ? handleRemoveHoldToggleSwitch
                        : handleHoldToggleSwitch
                    }
                  />
                )
              }
              label={
                isOrderOnHold ? "ORDER LOCKED" : "LOCK ORDER ON PRODUCTION"
              }
            />
          </div>
        )}
      </div>
      <RequestArtworkChangeModal
        isOpen={isRequestingArtworkChange}
        onRequestClose={handleRequestArtworkChangeModalRequestClose}
        orderId={orderId}
      />
      <PlaceOrderOnHoldModal
        isOpen={isPlacingOrderOnHold}
        onRequestClose={handlePlaceOrderOnHoldModalRequestClose}
        orderId={orderId}
      />
    </div>
  );
};

export default withSnackbar(ArtOrderStatus);
